<template>
    <b-container fluid class="py-3 flex-grow-1 d-flex flex-column">
        <section class="grid-filters">
            <b-row align-h="between" class="mb-3">
                <b-col
                        cols="12"
                        sm="5"
                        md="4"
                        lg="3"
                        xl="4"
                        class="order-0 d-sm-block mb-3 mb-sm-0"
                        :class="{'d-none': !showSearch}"
                >
                    <search-filter
                            :showSearch="showSearch"
                            v-model="query.search"
                            @submit="submitSearchBox"
                            :placeholder="requiresServersideSearch ? 'Search for Action ID or UPRN' : 'Search'"
                    >
                      <b-button v-if="requiresServersideSearch"
                                @click="refresh"
                                variant="tertiary"
                                size="sm"
                                class="ml-2">Find</b-button>
                    </search-filter>
                    <div class="mt-2" v-if="requiresServersideSearch">We'll be searching all actions in our database</div>

                  <div class="mt-4" v-if="gridTitle">
                    <h4>{{ gridTitle }}</h4>
                  </div>

                </b-col>
                <b-col cols="12" sm="7" md="8" lg="9" xl="8">
                    <b-row no-gutters class="justify-content-end">
                        <b-col col sm="auto" class="pr-1">
                            <b-button
                                    v-if="!requiresServersideSearch"
                                    :disabled="!this.isOpenActionsPage"
                                    variant="primary"
                                    class="text-center w-100 text-nowrap py-1 px-2"
                                    id="approveButton"
                                    @click="toggle.approved.value = !toggle.approved.value"
                            >
                                <font-awesome-icon
                                        :icon="['far', 'eye' + (!toggle.approved.value ? '-slash' : '')]"
                                        class="mr-1"
                                ></font-awesome-icon>
                                {{ toggle.approved.value ? 'Hide' : 'Show' }} Approved
                            </b-button>
                        </b-col>
                        <b-col col sm="auto" class="pl-1 d-none d-md-block">
                            <b-button
                                v-if="!requiresServersideSearch"

                                variant="tertiary"
                                    class="text-left w-100 text-nowrap py-1 px-2 filters-toggle"
                                    @click="toggle.filters.value = !toggle.filters.value"
                            >
                                <font-awesome-icon
                                    :icon="['fas', 'filter']"
                                    class="mr-1"
                                ></font-awesome-icon>
                                {{ toggle.filters.value ? 'Hide' : 'Show' }} Filters
                            </b-button>
                        </b-col>
                        <b-col col sm="auto" class="pl-1 d-none d-md-block">
                          <portal-target v-show="!actionsMenuVisible" name="actions-subnav" />
                        </b-col>
                        <b-col col sm="auto" class="pl-1 d-block d-md-none">
                            <b-button
                                    variant="tertiary"
                                    class="text-center w-100 text-nowrap py-1 px-2"
                                    v-b-modal.quickFiltersModal
                            >
                                <font-awesome-icon
                                        :icon="['fas', 'filter']"
                                        class="mr-1"
                                ></font-awesome-icon>
                                Quick Filters
                            </b-button>
                        </b-col>
                    </b-row>
                    <b-row no-gutters class="justify-content-end d-none d-md-block" v-if="toggle.filters.value">
                        <b-col cols="12" class="pt-3 text-right filters-top">

                                <multiselect
                                        v-if="!requiresServersideSearch"
                                        :hide-selected="true"
                                        :key="filter.field"
                                        :multiple="filter.multiple"
                                        :options="filter.options"
                                        :placeholder="filter.placeholder"
                                        :value="filter.value"
                                        class="hide-tags"
                                        selectLabel=""
                                        v-for="filter in getFilters"
                                        v-model="filter.value"
                                        :style="((filter.field == 'workPlanName' || filter.field == 'filterPlanDueDate') &&
                                        !($can($perm.ACTIONS_MANAGE_WORK_PLANS, $perm.PERMISSION_TYPE_ACTION)) ) ? 'display:none' : ''">
                                    <template #caret="{ toggle }">
                                        <caret-icon @mousedown.prevent.stop="toggle"></caret-icon>
                                    </template>
                                </multiselect>
                        </b-col>
                    </b-row>
                    <b-modal
                            id="quickFiltersModal"
                            static
                            :title="`Quick Filters`"
                            body-class="d-flex flex-column"
                            ok-title="Apply"
                            @ok="applyModalFilters"
                            @show="onModalFilterShow"
                            cancel-variant="outline-secondary"
                    >
                        <b-form-group
                                :label="filter.placeholder"
                                v-for="(filter, key) in filters"
                                :key="filter.field"
                                label-size="sm"
                        >
                            <multiselect
                                    :id="key"
                                    class="small w-100"
                                    v-model="filter.modalValue"
                                    :multiple="filter.multiple"
                                    :options="filter.options"
                                    :placeholder="filter.placeholder | filterPlaceholder"
                                    :hide-selected="filter.multiple"
                                    allowEmpty
                                    selectLabel=""
                            >
                                <template #caret="{ toggle }">
                                    <caret-icon @mousedown.prevent.stop="toggle"></caret-icon>
                                </template>
                            </multiselect>
                        </b-form-group>
                    </b-modal>
                </b-col>
            </b-row>
            <portal to="actions-subnav" v-if="showActionsDropdown()">
                <b-dropdown
                        no-caret
                        right
                        variant="outline-primary"
                        toggle-class="py-1 px-2"
                >
                    <template slot="button-content">
                        Actions
                        <font-awesome-icon
                                class="ml-1"
                                :icon="['fal', 'chevron-down']"
                                size="xs"
                        ></font-awesome-icon>
                    </template>
                    <b-dropdown-item
                            v-if="$can(
                              $perm.ACTIONS_ADD_CLIENT_ACTIONS,
                              $perm.PERMISSION_TYPE_ACTION,
                            )"
                            @click="addClientAction"
                    >
                        <font-awesome-icon
                                :icon="['fal', 'plus']"
                        ></font-awesome-icon>
                        Add Client Action
                    </b-dropdown-item>
                    <b-dropdown-item
                            @click="openModal('clientActionList')"
                            v-if="showViewClientActionsMenuOption"
                            :show.sync="this.hasLinkedClientActions"
                    >
                        <font-awesome-icon
                                :icon="['far', 'external-link-square']"
                                size="sm"
                        ></font-awesome-icon>
                        View Client Actions
                    </b-dropdown-item>
                    <b-dropdown-item
                            @click="openModal('assign')"
                            v-if="$can($perm.ACTIONS_ASSIGN, $perm.PERMISSION_TYPE_ACTION)"
                    >
                        <font-awesome-icon
                                :icon="['fal', 'users']"
                                size="sm"
                        ></font-awesome-icon>
                        Assign Selected Actions
                    </b-dropdown-item>
                    <b-dropdown-item
                            @click="openModal('queries')"
                            v-if="$can($perm.ACTIONS_BULK_QUERY, $perm.PERMISSION_TYPE_ACTION)"
                    >
                        <font-awesome-icon
                                :icon="['far', 'question-circle']"
                                size="sm"
                        ></font-awesome-icon>
                        Query Selected Actions
                    </b-dropdown-item>
                    <b-dropdown-item
                            @click="openModal('approve')"
                            v-if="$can(
                      $perm.ACTIONS_BULK_APPROVE_SUBMITTED,
                      $perm.PERMISSION_TYPE_ACTION,
                    )
                      || $can(
                        $perm.ACTIONS_BULK_APPROVE_ADMIN,
                        $perm.PERMISSION_TYPE_ACTION,
                      )"
                    >
                        <font-awesome-icon
                                :icon="['fal', 'check']"
                                size="sm"
                        ></font-awesome-icon>
                        Approve Selected Actions
                    </b-dropdown-item>
                    <b-dropdown-item
                            @click="openModal('unapprove')"
                            v-if="$can(
                        $perm.ACTIONS_BULK_UNAPPROVE_ADMIN,
                        $perm.PERMISSION_TYPE_ACTION,
                      )"
                    >
                        <font-awesome-icon
                                :icon="['fal', 'check']"
                                size="sm"
                        ></font-awesome-icon>
                        Unapprove Selected Actions
                    </b-dropdown-item>
                    <b-dropdown-item
                            @click="openModal('workOrders')"
                            v-if="$can($perm.ACTIONS_EDIT_WORK_ORDERS, $perm.PERMISSION_TYPE_ACTION)"
                    >
                        <font-awesome-icon
                                :icon="['fal', 'download']"
                                size="sm"
                        ></font-awesome-icon>
                        Add Work Order to Items
                    </b-dropdown-item>
                    <b-dropdown-item
                            @click="openModal('note')"
                            v-if="$can($perm.ACTIONS_BULK_ADD_NOTE, $perm.PERMISSION_TYPE_ACTION)"
                    >
                        <font-awesome-icon
                                :icon="['fal', 'sticky-note']"
                                size="sm"
                        ></font-awesome-icon>
                        Add note to items
                    </b-dropdown-item>
                    <b-dropdown-item
                            @click="openModal('tags')"
                            v-if="$can($perm.ACTIONS_TAG, $perm.PERMISSION_TYPE_ACTION)"
                    >
                        <font-awesome-icon
                                :icon="['fal', 'tags']"
                                size="sm"
                        ></font-awesome-icon>
                        Tag Items
                    </b-dropdown-item>
                    <b-dropdown-item
                            @click="openModal('removeTags')"
                            v-if="$can($perm.ACTIONS_BULK_REMOVE_TAG, $perm.PERMISSION_TYPE_ACTION)"
                    >
                        <font-awesome-icon
                                :icon="['fal', 'tags']"
                                size="sm"
                        ></font-awesome-icon>
                        Remove Tag
                    </b-dropdown-item>
                  <b-dropdown-item
                      @click="openModal('actionWorkPlan')"
                      v-if="$can($perm.ACTIONS_MANAGE_WORK_PLANS, $perm.PERMISSION_TYPE_ACTION)"
                  >
                    <font-awesome-icon
                        :icon="['fal', 'calendar-plus']"
                        size="sm"
                    ></font-awesome-icon>
                    Add to work plan
                  </b-dropdown-item>
                  <b-dropdown-item
                      @click="openModal('removeFromWorkPlan')"
                      v-if="$can($perm.ACTIONS_MANAGE_WORK_PLANS, $perm.PERMISSION_TYPE_ACTION)"
                  >
                    <font-awesome-icon
                        :icon="['fal', 'calendar-minus']"
                        size="sm"
                    ></font-awesome-icon>
                    Remove from work plan
                  </b-dropdown-item>
                    <b-dropdown-item
                            @click="onClickDownloadCsv"
                            v-if="$can($perm.ACTIONS_DOWNLOAD_TO_CSV, $perm.PERMISSION_TYPE_ACTION)"
                    >
                        <font-awesome-icon
                                :icon="['fal', 'download']"
                                size="sm"
                        ></font-awesome-icon>
                        Download CSV
                    </b-dropdown-item>
                    <b-dropdown-item
                            @click="onClickDownloadPdf"
                            v-if="showDownloadPdf"
                    >
                        <font-awesome-icon
                                :icon="['fal', 'file-download']"
                                size="sm"
                        ></font-awesome-icon>
                        Download PDF
                    </b-dropdown-item>
                    <b-dropdown-item
                            @click="onClickDownloadAll"
                            v-if="$can($perm.ACTIONS_DOWNLOAD_ALL_ACTIONS, $perm.PERMISSION_TYPE_ACTION)"
                    >
                        <font-awesome-icon
                                :icon="['fal', 'download']"
                                size="sm"
                        ></font-awesome-icon>
                        Download All
                    </b-dropdown-item>
                </b-dropdown>
            </portal>
        </section>

        <b-row class="flex-grow-1 d-flex flex-column">
            <b-col class="flex-grow-1 d-flex flex-column">
                <b-row class="grid-count mb-3 justify-content-between align-items-baseline">
                    <b-col cols="3" class="d-flex align-items-baseline text-dark">
                        <span>
                          {{ count }} Results
                          <span class="font-weight-bold"
                                v-if="selectedCount > 0"
                          >
                            | {{ selectedCount }} selected
                          </span>
                        </span>
                    </b-col>
                    <b-col cols="9" class="d-flex justify-content-end items-center">
                        <div
                            v-if="showYearFilters"
                            class="d-flex items-start justify-start"
                            style="width: 100%"
                        >
                            <template v-for="(yearFilter, index) in yearFilters">
                              <div
                                  v-bind:key="index"
                                  class="mr-4 cursor-pointer pb-2 text-dark"
                                  :style="selectedYearFilterIndex === index
                                    ? 'border-bottom: 4px solid black !important;'
                                    : ''"
                                  @click="changeDateRange(yearFilter, index)"
                              >
                                <span>{{ yearFilter.label }}</span>
                              </div>
                            </template>
                        </div>
                        <div class="d-flex items-center justify-content-end">
                            <reset-filters
                                @click="resetGridFilters"></reset-filters>
                            <refresh-button
                                :spinning="refreshing"
                                @click="refresh"
                            ></refresh-button>
                        </div>
                    </b-col>
                </b-row>
                <div v-if="allFilters.length > 0" class="filters-all mb-2">
                    <div class="filters-all-items">
                        <b-badge :key="filter.label"
                                 @click="removeFilter(filter)"
                                 pill
                                 v-for="filter in allFilters"
                                 variant="secondary"
                                 class="mb-2"
                        >
                            {{ filter.label }}
                            <font-awesome-icon
                                    :icon="['fal', 'times-circle']"
                            ></font-awesome-icon>
                        </b-badge>
                        <span
                                @click="resetFilters"
                                class="filters-reset small"
                        >Reset all</span>
                    </div>
                </div>
                <div class="grid-wrapper flex-grow-1 d-flex">
                    <ag-grid-vue
                            :style="'width: ' + gridWidth +'%;'"
                            class="ag-theme-balham"

                            :cacheQuickFilter="cacheQuickFilter"
                            @cellClicked="openActionModal"

                            :columnDefs="columnDefs"
                            @columnMoved="saveGridOptions"
                            @columnPinned="saveGridOptions"
                            @columnResized="saveGridOptions"
                            :columnTypes="columnTypes"
                            @columnVisible="saveGridOptions"
                            :defaultColDef="defaultColDef"

                            :doesExternalFilterPass="doesExternalFilterPass"

                            @filterChanged="saveGridOptions"
                            :floatingFilter="floatingFilter"
                            :gridOptions="gridOptions"
                            @grid-ready="onGridReady"

                            :headerHeight="rowHeight"
                            :isExternalFilterPresent="isExternalFilterActive"
                            :quickFilter="query.search"
                            :rowData="rowData"

                            :rowHeight="rowHeight"
                            @rowSelected="updateRowSelected"

                            :rowSelection="rowSelection"
                            :side-bar="sideBarDef"
                            @selectionChanged="onSelectionChanged"
                            @sortChanged="saveGridOptions"

                            :suppressNoRowsOverlay="!requiresServersideSearch"
                            :suppressRowClickSelection="isAllRowsSelected"
                            :suppressExcelExport="suppressExcelExport"

                            :overlayNoRowsTemplate="requiresServersideSearch ? noRowsComponent : null"
                    >
                    </ag-grid-vue>
                </div>
            </b-col>
        </b-row>
        <add-client-action-modal
                v-if="modals.add.visible"
                :show.sync="modals.add.visible"
                :linkedAction="modals.add.linkedAction"
                :property="modals.add.property"
        ></add-client-action-modal>
        <remove-tag-modal
                :action-count="count"
                :node-ids="selected.nodeIds"
                :show.sync="modals.removeTags.visible"
                @refresh-all="refresh"
                @tag-all="selectAll"
                v-if="modals.removeTags.visible"
        ></remove-tag-modal>
        <action-work-plan-modal
            :invalidNodes="invalidNodes"
            :node-ids="selected.nodeIds"
            :nodes="validNodes"
            :action-count="count"
            :selected-count="selectedCount"
            :show.sync="modals.actionWorkPlan.visible"
            @refresh-all="refresh"
            @tag-all="selectAll"
            v-if="modals.actionWorkPlan.visible"
        ></action-work-plan-modal>
      <RemoveFromWorkPlanModal
            :action-count="count"
            :selected-count="selectedCount"
            :node-ids="selected.nodeIds"
            :nodes="validNodes"
            :show.sync="modals.removeFromWorkPlan.visible"
            @refresh-all="refresh"
            @tag-all="selectAll"
            v-if="modals.removeFromWorkPlan.visible"
        ></RemoveFromWorkPlanModal>
        <work-order-modal
          v-if="modals.workOrders.visible"
          :node-ids="selected.nodeIds"
          :action-count="count"
          :show.sync="modals.workOrders.visible"
          @refresh-all="refresh"
          :type="'riskhub'"
        ></work-order-modal>
        <tag-modal
                :action-count="count"
                :node-ids="selected.nodeIds"
                :show.sync="modals.tags.visible"
                @refresh-all="refresh"
                @tag-all="selectAll"
                v-if="modals.tags.visible"
        ></tag-modal>
        <action-assign-modal
                :action-count="count"
                :node-ids="selected.nodeIds"
                :show.sync="modals.assign.visible"
                @assign-all="selectAll"
                @refresh-all="refresh"
                v-if="modals.assign.visible"
        ></action-assign-modal>
        <approve-modal
                :invalid-status="invalidStatus"
                :invalidNodes="invalidNodes"
                :node-ids="selected.nodeIds"
                :nodes="validNodes"
                :show.sync="modals.approve.visible"
                @refresh-all="refresh"
                v-if="modals.approve.visible"
                type="riskhub"
        ></approve-modal>
        <unapprove-modal
                :invalidNodes="invalidNodes"
                :node-ids="selected.nodeIds"
                :nodes="validNodes"
                :show.sync="modals.unapprove.visible"
                @refresh-all="refresh"
                v-if="modals.unapprove.visible"
        ></unapprove-modal>
        <submit-bulk-queries-modal
                :client-action-ids="selected.nodeIds"
                :show.sync="modals.queries.visible"
                v-if="modals.queries.visible"
        ></submit-bulk-queries-modal>
        <note-modal
                :title="modals.note.settings.noteTitle"
                :show.sync="modals.note.visible"
                :client-action-ids="selected.nodeIds"
                :busy="modals.note.loading"
                :sentNoteFn="modals.note.settings.sentNoteFn"
                @file-sent="modals.note.settings.onSentFileFn"
                :file-url="modals.note.settings.fileUrl"
                :note-placeholder="modals.note.settings.notePlaceholder"
                :button-text="modals.note.settings.noteButtonText"
                :error-message="modals.note.settings.noteErrorMessage"
        ></note-modal>
        <client-action-list-modal
                :show.sync="modals.clientActionsList.visible"
                :actionId="modals.clientActionsList.actionId"
        >
        </client-action-list-modal>

        <router-view name="actionPage"></router-view>
    </b-container>
</template>


<script lang="js">
import { AgGridVue } from 'ag-grid-vue';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import { filter as objFilter, forEach, size } from 'lodash';
import { mapGetters } from 'vuex';
import {
  actionGridColumnTypes,
  actionGridDefaultColDef,
  getActionGridColumns,
  queryGridColumns,
} from '@/services/Action/ActionGridConfig';
import CaretIcon from '@/components/Form/CaretIcon';
import Action from '@/services/Action/Action';
import { default as ActionFilter, fraAndWorkplanDueDates, workPlanFilter } from '@/services/Action/ActionFilter';
import debounce from 'lodash/debounce';
import TagModal from '@/components/Action/Modal/TagModal';
import ActionAssignModal from '@/components/Action/Modal/ActionAssignModal';
import RemoveTagModal from '@/components/Action/Modal/RemoveTagModal';
import ApproveModal from '@/components/Action/Modal/ApproveModal';
import UnapproveModal from '@/components/Action/Modal/UnapproveModal';
import ActionModal from '@/components/Action/Modal/ActionModal';
import ActionStatus from '@/services/Action/ActionStatus';
import NoteModal from '@/components/Action/Modal/NoteModal';
import ActionWorkPlanModal from '@/components/Action/Modal/ActionWorkPlanModal';
import RemoveFromWorkPlanModal from '@/components/Action/Modal/RemoveFromWorkPlanModal';
import GridMixin from '@/components/Grid/GridMixin';
import api from '@/api';
import ResetFilters from '@/components/Grid/ResetFilters';
import RefreshButton from '@/components/Grid/RefreshButton';
import SearchFilter from '@/components/Grid/SearchFilter';
import ActionType from '@/services/Action/ActionType';
import AddClientActionModal from '@/components/Action/Modal/AddClientActionModal';
import SubmitBulkQueriesModal from '@/components/Action/Modal/SubmitBulkQueriesModal';
import ErrorMixin from '@/components/Notifications/ErrorMixin';
import ClientActionListModal from '@/components/Action/Modal/ClientActionsListModal';
import riskhub from '@/services/Riskhub/Riskhub';
import WorkOrderModal from "@/components/Action/Modal/BulkEditWorkOrderModal.vue";

dayjs.extend(isBetween);
dayjs.extend(isSameOrAfter);

const FILTER_SET = 'SET';
const FILTER_TEXT = 'TEXT';
const LINKED_CLIENT_ACTIONS_COLUMN = 'numOfLinkedClientActions';
const CLIENT_ACTION_LIST_MODAL_ID = 'clientActionList';
export default {
  name: 'Actions',
  components: {
    WorkOrderModal,
    SubmitBulkQueriesModal,
    AgGridVue,
    CaretIcon,
    TagModal,
    ActionAssignModal,
    RemoveTagModal,
    ApproveModal,
    UnapproveModal,
    ActionModal,
    NoteModal,
    ResetFilters,
    RefreshButton,
    SearchFilter,
    AddClientActionModal,
    ClientActionListModal,
    ActionWorkPlanModal,
    RemoveFromWorkPlanModal,
  },
  props: {
    title: {
      type: String,
    },
  },
  mixins: [
    GridMixin,
    ErrorMixin,
  ],
  data() {
    return {
      // This is for the API cancel request
      modelId: 'clientActionId',
      loading: false,
      actionType: ActionType.getTypeName(ActionType.RISKHUB()),
      cancelSource: null,
      ignoreWatches: false,
      currentRequestId: null,
      filters: {
        workplan: {
          type: FILTER_SET,
          placeholder: 'Filter Work Plan',
          field: 'workPlanName',
          multiple: true,
          options: [],
          value: [],
          modalValue: [],
          fill: true,
        },
        team: {
          type: FILTER_SET,
          placeholder: 'Filter Team',
          field: 'currentResponsibleTeam',
          multiple: true,
          options: [],
          value: [],
          modalValue: [],
          fill: true,
        },
        user: {
          type: FILTER_SET,
          placeholder: 'Filter User',
          field: 'reviewer',
          multiple: true,
          options: [],
          value: [],
          modalValue: [],
          fill: true,
        },
        planduedate: {
          field: 'filterPlanDueDate',
          placeholder: 'Filter Plan Due Date',
          multiple: false,
          external: true,
          filterFunction: node => this.filterPlanOverdue(node),
          options: workPlanFilter.options(),
          mapping: workPlanFilter.mapping(),
          value: [],
          modalValue: [],
        },
        fraPlanduedate: {
          field: 'filterFraPlanDueDate',
          placeholder: 'Filter FraPlan Due Date',
          multiple: false,
          external: true,
          filterFunction: node => this.filterFraPlanOverdue(node),
          options: fraAndWorkplanDueDates.options(),
          mapping: fraAndWorkplanDueDates.mapping(),
          value: [],
          modalValue: [],
          hide: true,
        },
        overdue: {
          placeholder: 'Filter FRA Action Due Dates',
          multiple: false,
          external: true,
          filterFunction: node => this.filterOverdue(node),
          options: [
            ActionFilter.FILTER_DATE_OVERDUE_FRA(),
            ActionFilter.FILTER_DATE_OVERDUE_1_30(),
            ActionFilter.FILTER_DATE_OVERDUE_31_60(),
            ActionFilter.FILTER_DATE_OVERDUE_61_90(),
            ActionFilter.FILTER_DATE_OVERDUE_OVER_90(),
            ActionFilter.FILTER_DATE_OVERDUE_INTERNAL(),
            ActionFilter.FILTER_DATE_WITHIN_FRA(),
            ActionFilter.FILTER_DATE_WITHIN_INTERNAL(),
            ActionFilter.FILTER_DATE_WITHIN_1_30(),
            ActionFilter.FILTER_DATE_WITHIN_31_60(),
            ActionFilter.FILTER_DATE_WITHIN_61_90(),
            ActionFilter.FILTER_DATE_WITHIN_OVER_90(),
          ],
          mapping: {
            1: ActionFilter.FILTER_DATE_OVERDUE_FRA(),
            2: ActionFilter.FILTER_DATE_WITHIN_FRA(),
            3: ActionFilter.FILTER_DATE_OVERDUE_INTERNAL(),
            4: ActionFilter.FILTER_DATE_OVERDUE_1_30(),
            5: ActionFilter.FILTER_DATE_OVERDUE_31_60(),
            6: ActionFilter.FILTER_DATE_OVERDUE_61_90(),
            7: ActionFilter.FILTER_DATE_OVERDUE_OVER_90(),
            8: ActionFilter.FILTER_DATE_WITHIN_1_30(),
            9: ActionFilter.FILTER_DATE_WITHIN_31_60(),
            10: ActionFilter.FILTER_DATE_WITHIN_61_90(),
            11: ActionFilter.FILTER_DATE_WITHIN_OVER_90(),
          },
          value: [],
          modalValue: [],
        },
        tag: {
          type: FILTER_SET,
          placeholder: 'Filter Tag',
          field: 'tags',
          multiple: true,
          options: [],
          value: [],
          modalValue: [],
          fill: false,
        },
      },
      toggle: {
        approved: {
          value: false,
        },
        filters: {
          value: true,
        },
      },
      defaultColDef: actionGridDefaultColDef,
      columnTypes: actionGridColumnTypes,
      columnDefs: null,
      validNodes: [],
      invalidNodes: [],
      invalidStatus: '',
      hasLinkedClientActions: false,
      arrivedFromWorkPlansPage: false,
      modals: {
        note: {
          visible: false,
          loading: false,
          settings: {
            noteTitle: 'Add a note',
            noteButtonText: 'Send note',
            sentNoteFn: null,
            notePlaceholder: null,
            noteErrorMessage: null,
            onSentFileFn: () => null,
            showNoteModal: null,
            fileUrl: null,
          },
        },
        workOrders: {
          visible: false,
        },
        action: {
          visible: false,
        },
        tags: {
          visible: false,
        },
        removeTags: {
          visible: false,
        },
        assign: {
          visible: false,
        },
        approve: {
          visible: false,
        },
        unapprove: {
          visible: false,
        },
        add: {
          visible: false,
          linkedAction: null,
          property: null,
        },
        clientActionsList: {
          visible: false,
          actionId: null,
        },
        queries: {
          visible: false,
        },
        actionWorkPlan: {
          visible: false,
        },
        removeFromWorkPlan: {
          visible: false,
        },
      },
      yearRange: {
        start: dayjs().startOf('year').toDate(),
        end: dayjs().endOf('day').toDate(),
        label: `${dayjs().startOf('year').format('MMM YYYY')} to date`,
      },
      yearFilters: [
        {
          start: dayjs().startOf('year').toDate(),
          end: dayjs().endOf('day').toDate(),
          label: `${dayjs().startOf('year').format('MMM YYYY')} to date`,
        },
        {
          start: dayjs().startOf('year').subtract(1, 'year').toDate(),
          end: dayjs().endOf('year').subtract(1, 'year').toDate(),
          label: dayjs().startOf('year').subtract(1, 'year').format('YYYY'),
        },
        {
          start: dayjs().startOf('year').subtract(2, 'year').toDate(),
          end: dayjs().endOf('year').subtract(2, 'year').toDate(),
          label: dayjs().startOf('year').subtract(2, 'year').format('YYYY'),
        },
        {
          start: dayjs().startOf('day').subtract(1, 'year').toDate(),
          end: dayjs().endOf('day').toDate(),
          label: '1 year of actions to date',
        },
      ],
      selectedYearFilterIndex: 0,
      previousRoute: {},
    };
  },

  computed: {
    ...mapGetters({
      showSearch: 'actionShowSearch',
      actionRefresh: 'actionRefresh',
      statusFilter: 'actionStatusFilter',
      optionTags: 'optionTags',
      actionsMenuVisible: 'actionsMenuVisible',
      workPlanItemsToEdit: 'workPlanItemsToEdit',
      userClientId: 'userClientId',
      counts: 'counts'
    }),

    gridTitle() {
      if (this.isActionModalPageOpen && this.previousRoute) {
        if (this.previousRoute.name === 'actionFinder') {
          return 'Action Finder';
        }

        if (this.previousRoute.name === 'actions'
          && this.previousRoute.params.statusFilter === undefined) {
          return 'Open';
        }
      }

      if (this.isOpenActionsPage) {
        return 'Open';
      }

      if (this.title) {
        return this.title;
      }

      if (this.isFinderPage) {
        return 'Action Finder';
      }

      if (this.statusFilter) {
        return ActionStatus.getTitle(this.statusFilter)
      }

      const { overdue, status, planduedate } = this.$route.query;

      // Get title for overdue param
      if (overdue) {
        return this.filters.overdue.mapping[overdue];
      }

      // Get title for planduedate param
      if (planduedate) {
        return this.filters.fraPlanduedate.mapping[planduedate];
      }

      // Get title for status param
      if (status) {
        const label = ActionStatus.getLabelForSecondaryOrCompletionStatus(status);

        if (label) {
          return this.uppercaseFirst(label);
        }

        if (Array.isArray(status)) {
          return this.uppercaseFirst(status.join(', '));
        }

        return this.uppercaseFirst(status);
      }

      // Otherwise return first item as title
      return Object.values(this.$route.query)[0] || null
    },

    /**
     * This combines all the filters that are applied via external dropdowns
     * It is used mainly for visual purposes
     * * */
    allFilters() {
      const allFilters = [];

      // Iterate over the filters
      for (const [type, item] of Object.entries(this.filters)) {
        // If the value is not an array, push it as a single filter
        if (!Array.isArray(item.value)) {
          allFilters.push({
            type,
            index: 0,
            label: item.value,
          });
          continue;
        }

        // Iterate over the values of the array and push each one as a filter
        let i = 0;
        for (const label of item.value) {
          allFilters.push({
            type,
            index: i++,
            label,
          });
        }
      }

      return allFilters;
    },

    showDownloadPdf() {
      return !!(this.$can(this.$perm.ACTIONS_DOWNLOAD_TO_PDF, this.$perm.PERMISSION_TYPE_ACTION)
          && !this.statusFilter);
    },
    showViewClientActionsMenuOption() {
      return this.showViewClientActions(this.selectedIds);
    },

    showYearFilters() {
      return false;
      // TODO: Re-enable for improvements to approved actions
      //return this.statusFilter === 'approved';
    },

    getFilters() {
      const filters = {};

      for (const [type, item] of Object.entries(this.filters)) {
        if (!item.hide) {
          filters[type] = item;
        }
      }

      return filters;
    },

    /**
     * Returns true if we're on the route or if it is the active screen behind the
     * action page (modal)
     * @returns {boolean|*|{}}
     */
    isOpenActionsPage() {
      return this.$route.name === 'actions'
          && (this.$route.params.statusFilter === undefined || this.$route.params.statusFilter === 'open')
              || (this.isActionModalPageOpen
              && this.$route.params.statusFilter === undefined
              && this.previousRoute && this.previousRoute.name === 'actions'
          );
    },

    /**
     * Returns true if we're on the route or if it is the active screen behind the
     * action page (modal)
     * @returns {boolean|*|{}}
     */
    isFinderPage() {
      return this.$route.name === 'actionFinder'
            || (this.isActionModalPageOpen
              && ActionStatus.getRouteByStatus(null) === 'actionFinder'
              && this.previousRoute && this.previousRoute.name === 'actionFinder');
    },

    isActionModalPageOpen() {
      return this.$route.params.clientActionId !== null && this.$route.name === 'actionPage';
    },

    /**
     * Determines whether we do a serverside search based on action count
     * on the page we are on
     * @returns {boolean}
     */
    requiresServersideSearch() {
      return this.isFinderPage;
    },

    totalCount() {
      return this.counts[this.statusFilter];
    },

    /**
     * Used for displaying a string on the AG grid when there are no rows available
     * @returns {null|string}
     */
    noRowsComponent() {
      if (!this.requiresServersideSearch) {
        return null;
      }

      let text = 'Please search for a UPRN or an Action ID';

      if (this.query.search !== '') {
          text = 'No results found. <br><br>' +
            'Please make sure the Action ID or UPRN is valid and try again.';
      }

      return '<div role="alert" aria-live="polite" aria-atomic="true" ' +
          'class="alert alert-info text-center">' +
          text +
          '</div>'
    }
  },

  watch: {
    // We check the route to see if the status has changed via the status filter parameter
    // We set the state status filter, which triggers the table to refresh
    // We do not want to set this if the route is loading an individual action
    $route: {
      handler(route, oldRoute) {
        this.previousRoute = oldRoute;
        if (route.params.clientActionId) {
          return;
        }

        if (this.isFinderPage || this.isOpenActionsPage) {
          this.$store.dispatch('setActionsFilter', { status: null });

          // Don't reset the page if we're coming from an action
          if (oldRoute && !oldRoute.params.clientActionId) {
            return this.resetPage();
          }

          return;
        }

        const { statusFilter } = route.params;
        this.$store.dispatch('setActionsFilter', { status: ActionStatus.getStatusByRoute(statusFilter) });
      },
      immediate: true,
    },
    async statusFilter(newValue, oldValue) {
      if (newValue === oldValue) {
        return;
      }
      // Wait for any extra components to render first before refreshing
      await this.resetPage();
    },
    actionRefresh(refresh) {
      if (refresh) {
        this.refresh();
        this.$store.dispatch('refreshActionsComplete');
      }
    },
    '$store.getters.complianceTypeId': function () {
      this.refresh();
    },
    '$store.getters.propertyTenure': function () {
      this.refresh();
    },
    'filters.team.value': function () {
      this.onFilterChange(this.filters.team);
    },
    'filters.workplan': {
      handler() {
        if (this.arrivedFromWorkPlansPage && !this.filters.workplan.value.length) {
          this.arrivedFromWorkPlansPage = false;

          if (this.toggle.approved.value) {
            // this will call the toggle approved filter, which in turn will refresh the data
            this.toggle.approved.value = false;
          } else {
            this.refresh();
          }
        } else {
          this.onFilterChange(this.filters.workplan);
        }
      },
      deep: true,
    },
    'filters.user.value': function () {
      this.onFilterChange(this.filters.user);
    },
    'filters.tag.value': function () {
      this.onFilterChange(this.filters.tag);
    },
    'filters.overdue.value': function () {
      this.gridApi.onFilterChanged();
    },
    'filters.planduedate.value': function () {
      this.gridApi.onFilterChanged();
    },
    'filters.fraPlanduedate.value': function () {
      this.gridApi.onFilterChanged();
    },

    'toggle.approved.value': function toggleApprovedValue() {
      if (this.workPlanItemsToEdit) {
        return true;
      }
      this.refresh();
    },
    'toggle.responseStatusOnly.value': function toggleResponseStatusOnlyValue() {
      this.gridApi.onFilterChanged();
    },
    'toggle.filters.value': function toggleShowFiltersValue() {
      // this.gridApi.onFilterChanged();
    },
    userClientId() {
      this.$store.dispatch('resetLocalStorageGridOptions', 'actions');
    },
  },

  filters: {
    filterPlaceholder(placeholder) {
      return `Select ${placeholder.replace(/Filter/, '')}`;
    },
  },

  async created() {
    if (!this.$route.params.statusFilter) {
      this.$store.dispatch('setActionsFilter', { status: null });
    }
  },

  async mounted() {
    // await this.getApprovedYears();
    this.onMountQueryParams();
    this.$store.dispatch('refreshTags')
      .then(() => {
        this.refreshTagFilter();
      });
  },

  methods: {
    async getApprovedYears() {
      // this.yearFilters = await Action.getApprovedYears();
    },
    async postGridReady(params) {
      this.setRowClasses();
      this.refresh();
    },

    /**
    * Resets grid to absolute default
    */
    preResetGridFilters() {
      this.resetting = true;
      forEach(this.filters, (filter, type) => {
        this.filters[type].value = [];
      });

      if (Object.entries(this.$router.currentRoute.query).length) {
        this.$router.replace({ query: null }).catch((err) => {});
      }
    },

    postResetGridFilters() {
      this.$nextTick(() => {
        this.resetting = false;
      });
    },

    /**
     * Set the grid columns based on permissions / status
     * */
    setGridColumns() {
      return new Promise((resolve) => {
        let columnDefs = getActionGridColumns().filter(this.filterColumn);

        // We have some specific columns to add if we're on the query status page
        if (this.statusFilter === ActionStatus.QUERY()) {
          columnDefs = [...columnDefs, ...queryGridColumns];
        }

        this.columnDefs = columnDefs;

        this.$nextTick(() => {
          resolve();
        });
      });
    },

    /**
    * Filter columns based on permissions
    *
    * @param {Object} column
    */
    filterColumn(column) {
      if (column.field === 'dueDate') {
        return this.$can(
          this.$perm.ACTIONS_VIEW_ACTION_DUE_DATE,
          this.$perm.PERMISSION_TYPE_ACTION,
        );
      }
      if (column.field === LINKED_CLIENT_ACTIONS_COLUMN) {
        return this.$can(
          this.$perm.ACTIONS_VIEW_CLIENT_ACTIONS,
          this.$perm.PERMISSION_TYPE_ACTION,
        );
      }
      if (column.field === 'workOrder') {
        return this.$can(
          this.$perm.ACTIONS_VIEW_WORK_ORDERS,
          this.$perm.PERMISSION_TYPE_ACTION,
        );
      }
      return true;
    },

    setNewColumnCorrectLocation(
      gridState,
      newColumnId,
      columnToLeftOfNewColumnId,
      newColumnWidth,
      hide = true,
    ) {
      if (typeof gridState.find === 'function' && !gridState.find(col => col.colId === newColumnId)) {
        const leftColumnIndex = gridState.findIndex(col => col.colId === columnToLeftOfNewColumnId);
        if (leftColumnIndex !== -1) {
          gridState = [
            ...gridState.slice(0, leftColumnIndex + 1),
            {
              aggFunc: null,
              colId: newColumnId,
              hide,
              pinned: null,
              pivotIndex: null,
              rowGroupIndex: null,
              width: newColumnWidth,
            },
            ...gridState.slice(leftColumnIndex + 1),
          ];
        }
      }
      return gridState;
    },

    /**
    * Sets the grid up with:
    * Column state
    * Sort
    * Filters
    */
    setGridOptions() {
      const name = this.statusFilter || 'actions';

      let gridState = this.$store.getters.gridState(name);
      let gridSort = this.$store.getters.gridSort(name);
      let gridFilter = this.$store.getters.gridFilter(name);

      if (!gridState) {
        gridState = this.gridDefaults.state;
      }

      if (!gridSort) {
        gridSort = this.gridDefaults.sort;
      }

      if (!gridFilter) {
        gridFilter = this.gridDefaults.filter;
      }

      gridState = this.setNewColumnCorrectLocation(gridState, 'submittedForApprovalDate', 'submittedByTeam', 250, true);

      gridState = this.setNewColumnCorrectLocation(gridState, 'location', 'question', 150, true);

      try {
        if (Object.keys(gridState).length > 0) {
          this.columnApi.setColumnState(gridState);
        }
      } catch (e) {
        // ignore errors - if the column state is invalid, we don't want to break the grid
      }

      if (this.hasQueryString()) {
        return;
      }

      this.gridApi.setSortModel(gridSort);
      this.gridApi.setFilterModel(gridFilter);
      this.gridApi.onFilterChanged();
      this.setTopFilterValues(gridFilter);
    },

    // Sets the filters at the top, which displays a visual representation
    // If grid options are saved, they can be reloaded in and set so the top level filters match too
    // Since all they do is trigger AG Grid's filters anyway
    setTopFilterValues(gridFilters) {
      forEach(gridFilters, (options, filterName) => {
        const matchFilter = objFilter(this.filters, f => f.field === filterName);

        if (matchFilter[0]) {
          matchFilter[0].value = options.values;
        }
      });
    },

    /**
    * Only allow to save the grid if it is not refreshing data
    * */
    saveGridOptions() {
      if (this.refreshing) {
        return false;
      }

      if (this.hasQueryString()) {
        return false;
      }

      return this.saveOptions();
    },

    onSelectionChanged() {
      this.hasLinkedClientActions = false;
      const selectedNodes = this.gridApi.getSelectedNodes();
      if (selectedNodes.length === 1 && selectedNodes[0].data.numOfLinkedClientActions > 0) {
        this.hasLinkedClientActions = true;
        this.showViewClientActions(selectedNodes);
      }
    },

    showViewClientActions(selected) {
      return this.$can(this.$perm.ACTIONS_VIEW_CLIENT_ACTIONS, this.$perm.PERMISSION_TYPE_ACTION)
        && selected.length === 1 && this.hasLinkedClientActions;
    },

    /**
    * Save the grid options (only allowed once per second)
    */
    saveOptions: debounce(function () {
      const state = this.columnApi.getColumnState();
      const sort = this.gridApi.getSortModel();
      const filter = this.gridApi.getFilterModel();

      const name = this.statusFilter || 'actions'

      this.$store.dispatch('saveGridOptions', {
        name,
        state,
        sort,
        filter,
      });
    }, 1000),

    /**
     * Checks if an external filter is active
     */
    isExternalFilterActive() {
      return Object.entries(this.filters)
        .some(([type, item]) =>
          item.external
            && item.value
            && Object.entries(item.value).length > 0);
    },

    /**
     * Checks if all external filters pass (e.g., being overdue)
     */
    doesExternalFilterPass(node) {
      const filterResults = Object.entries(this.filters)
        .filter(([type, item]) =>
          item.external
            && item.filterFunction
            && item.value
            && Object.entries(item.value).length > 0)
        .map(([type, item]) => item.filterFunction(node));

      return filterResults.every(result => result === true);
    },

    /**
    * Checks each row of data if they are overdue
    * This is based on the dueDate and internalDueDate
    */
    filterOverdue(node) {
      const overdueFilter = this.filters.overdue.value;
      const now = dayjs();
      const dueDate = dayjs(node.data.dueDate);
      const internalDueDate = dayjs(node.data.internalDueDate);
      const hasDueDate = (node.data.dueDate && node.data.dueDate !== '0000-00-00');

      if (overdueFilter === ActionFilter.FILTER_DATE_OVERDUE_FRA()) {
        return dueDate.isBefore(now, 'day');
      }

      if (overdueFilter === ActionFilter.FILTER_DATE_OVERDUE_1_30()) {
        if (!hasDueDate) {
          return false;
        }
        const dateStart = now.subtract(30, 'day');
        const dateEnd = now.subtract(1, 'day');
        return dueDate.isBetween(dateEnd, dateStart, 'day', '[]');
      }

      if (overdueFilter === ActionFilter.FILTER_DATE_OVERDUE_31_60()) {
        if (!hasDueDate) {
          return false;
        }
        const dateStart = now.subtract(60, 'day');
        const dateEnd = now.subtract(31, 'day');
        return dueDate.isBetween(dateStart, dateEnd, 'day', '[]');
      }

      if (overdueFilter === ActionFilter.FILTER_DATE_OVERDUE_61_90()) {
        if (!hasDueDate) {
          return false;
        }
        const dateStart = now.subtract(90, 'day');
        const dateEnd = now.subtract(61, 'day');
        return dueDate.isBetween(dateStart, dateEnd, 'day', '[]');
      }

      if (overdueFilter === ActionFilter.FILTER_DATE_OVERDUE_OVER_90()) {
        const dateStart = now.subtract(90, 'day');
        return dueDate.isBefore(dateStart, 'day');
      }

      if (overdueFilter === ActionFilter.FILTER_DATE_OVERDUE_INTERNAL()) {
        return internalDueDate.isBefore(now, 'day');
      }

      if (overdueFilter === ActionFilter.FILTER_DATE_WITHIN_FRA()) {
        if (!hasDueDate) {
          return true;
        }

        return dueDate.isSameOrAfter(now, 'day');
      }

      if (overdueFilter === ActionFilter.FILTER_DATE_WITHIN_INTERNAL()) {
        return internalDueDate.isSameOrAfter(now, 'day');
      }

      if (overdueFilter === ActionFilter.FILTER_DATE_WITHIN_1_30()) {
        if (!hasDueDate) {
          return false;
        }
        const dateEnd = now.add(30, 'day');
        return dueDate.isBetween(now, dateEnd, 'day', '[]');
      }

      if (overdueFilter === ActionFilter.FILTER_DATE_WITHIN_31_60()) {
        if (!hasDueDate) {
          return false;
        }
        const dateStart = now.add(31, 'day');
        const dateEnd = now.add(60, 'day');
        return dueDate.isBetween(dateStart, dateEnd, 'day', '[]');
      }

      if (overdueFilter === ActionFilter.FILTER_DATE_WITHIN_61_90()) {
        if (!hasDueDate) {
          return false;
        }
        const dateStart = now.add(61, 'day');
        const dateEnd = now.add(90, 'day');
        return dueDate.isBetween(dateStart, dateEnd, 'day', '[]');
      }

      if (overdueFilter === ActionFilter.FILTER_DATE_WITHIN_OVER_90()) {
        if (!hasDueDate) {
          return true;
        }
        const dateStart = now.add(90, 'day');
        return dueDate.isAfter(dateStart, 'day');
      }

      return false;
    },
    filterPlanOverdue(node) {
      const overdueFilter = this.filters.planduedate.value;
      const dueDate = dayjs(node.data.workPlanDueDate);

      if (!dueDate.isValid() || !node.data.workPlanId) {
        return false;
      }

      return workPlanFilter.dateRanges(overdueFilter, dueDate);
    },

    filterFraPlanOverdue(node) {
      return fraAndWorkplanDueDates.dateRanges(this.filters.fraPlanduedate.value, node);
    },

    /**
     * Reset the table, do checks
     * @returns {Promise<boolean>}
     */
    async preRefresh() {
      // Reset
      this.rowData = null;

      // Grid hasn't loaded
      if (!this.gridApi) {
        return false;
      }

      // Get counts. If it's null then we don't have them and we require them for the serverside check
      this.totalCount === null
          ? await this.$store.dispatch('refreshCounts')
          : this.$store.dispatch('refreshCounts');

      // Dataset is too large, requires serverside search
      if (this.requiresServersideSearch && this.query.search.length < 2) {
        return false;
      }

      // Cancel any previous requests
      if (this.cancelSource) {
        this.cancelSource.cancel();
      }

      return true;
    },

    /**
    * Async
    * Refreshes the table
    * Sets the grid options afterwards
    * Using debounce to prevent multiple rapid calls when filters change quickly
    */
    refresh: debounce(async function() {
      if (!await this.preRefresh()) {
        return;
      }
      // Wait until the data is set, external dropdown filters are set
      await this.refreshData();
      // Then we set the grid options
      this.setGridOptions();
      // We set a timeout to disallow any future grid saving that happens
      // As filters are applied immediately after;
      this.postRefresh();
    }, 250),

    /**
       * Call after every refresh
       * */
    postRefresh() {
      this.postRefreshQueryParams();
      this.setDropdownFilters();
      this.updateRowSelected();
      this.gridApi.hideOverlay();
      this.refreshTagFilter();

      if (this.workPlanItemsToEdit) {
        this.filters.workplan.value = [];
        this.filters.workplan.modalValue = [];

        this.filters.workplan.value.push(this.workPlanItemsToEdit.name);
        this.filters.workplan.modalValue.push(this.workPlanItemsToEdit);
        this.onFilterChange(this.filters.workplan);

        this.$store.commit('setPlanItemsToEdit', null);
      }

      setTimeout(() => {
        this.refreshing = false;

        if (this.rowData.length === 0 && this.requiresServersideSearch) {
          this.rowData = null;
        }
      }, 0);
    },

    /**
    * Sets the rowdata with data from the server
    */
    async refreshData() {
      this.refreshing = true;

      if (this.workPlanItemsToEdit) {
        this.toggle.approved.value = true;
      }

      const params = this.getParams();
      params.params.limit = 7500;
      params.params.page = 1;

      this.cancelSource = api.CancelToken.source();

      const rowData = [];
      let total = 0;
      let requestId = Date.now();
      this.currentRequestId = requestId;

      const getActions = () => new Promise(async (resolve, reject) => {
        try {

          if (requestId !== this.currentRequestId) {
            return reject(new Error('Request superseded by newer request'));
          }

          const { data } = await Action.list(params, this.cancelSource);

          if (requestId !== this.currentRequestId) {
            return reject(new Error('Request superseded by newer request'));
          }

          const actions = data.data;

          total = data.meta.pagination.total;
          rowData.push(...actions);

          if (rowData.length >= total
            || data.meta.pagination.current_page === data.meta.pagination.total_pages
          ) {
            return resolve(rowData);
          }

          params.params.page += 1;
          await getActions(params);
          return resolve(rowData);
        } catch (error) {
          reject(error);
        }
      });

      try {
        this.rowData = await getActions();
      } catch (error) {
        if (error.message === 'Request superseded by newer request') {
          return;
        } else if (!api.isCancel(error)) {
          this.$notify({
            duration: 5000,
            title: 'Error',
            text: 'We couldn\'t obtain the actions right now. Please try again later.',
            type: 'error',
          });
        }
      }
    },

    /**
    * Set row classes
    */
    setRowClasses() {
      const now = dayjs();

      this.gridOptions.getRowClass = ({ data }) => {
        if (!data) {
          return '';
        }

        if (data.status !== ActionStatus.APPROVED()) {
          if (now.isAfter(dayjs(data.dueDate))) {
            return 'orange-row';
          }
          if (now.isAfter(dayjs(data.internalDueDate))) {
            return 'red-row';
          }
        }
        return '';
      };
    },

    /**
    * Check if the current route has a query string present
    */
    hasQueryString() {
      const { query } = this.$route;

      return (query && size(query) > 0);
    },

    /**
    * Anything to do with query params that should happen on mount
    */
    onMountQueryParams() {
      if (!this.hasQueryString()) {
        return;
      }

      const { query } = this.$route;

      forEach(query, (value, field) => {
        if (Array.isArray(value)) {
          value = value[0];
        }

        if (field === 'status'
            && value === ActionStatus.APPROVED()
        ) {
          this.toggle.approved.value = true;
        }
      });
    },

    /**
    * Apply any parameters from the query string to the grid
    */
    postRefreshQueryParams() {
      const { query } = this.$route;

      if (this.hasQueryString()) {
        forEach(query, (value, field) => {
          if (field === 'overdue') {
            this.filters.overdue.value = this.filters.overdue.mapping[value]
                || ActionFilter.FILTER_DATE_WITHIN_INTERNAL();

            return;
          } else if (field === 'planduedate') {
            this.filters.fraPlanduedate.value = this.filters.fraPlanduedate.mapping[value];
            return;
          }

          this.onFilterChange({
            field,
            value,
            type: FILTER_SET,
          });
        });
      }
    },

    /**
    * Gets any params for fetching data from the server
    */
    getParams() {
      const params = {};

      if (this.statusFilter) {
        params['filter[status]'] = this.statusFilter;
      }

      if (this.statusFilter !== 'approved'
          && !this.toggle.approved.value
          && !this.requiresServersideSearch) {
        params['filter[hideStatus]'] = 'approved';
      }

      if (this.workPlanItemsToEdit) {
        this.arrivedFromWorkPlansPage = true;
        params['filter[workPlanId]'] = this.workPlanItemsToEdit.workPlanId;
      }

      params.complianceTypeId = this.$store.getters.complianceTypeId;
      params.propertyTenure = this.$store.getters.propertyTenure;

      // TODO: Re-enable for improvements to approved actions
      // if (this.yearRange) {
      //   const start = dayjs(this.yearRange.start).format('YYYY-MM-DD');
      //   const end = dayjs(this.yearRange.end).format('YYYY-MM-DD');
      //   params['filter[completedDate]'] = `${start},${end}`;
      // }

      // Where LIKE server side search
      if (this.requiresServersideSearch) {
        const whereLike = [
          'clientActionId',
          'uprn',
        ];

        whereLike.forEach((field) => {
          params[`filter[like_${field}]`] = this.query.search;
        });
      }

      return {
        params,
      };
    },

    /**
    * Set all filters for external dropdowns
    */
    setDropdownFilters() {
      // Duplicate filter object
      const filters = Object.entries(this.filters)
        .filter(([type, item]) =>
          !item.external
            && item.type === FILTER_SET
            && item.fill);
        // Add a set
      filters.forEach(([type, item]) => {
        item.set = new Set();
      });

      // Loop through each row of data
      // Add field to a unique set
      this.rowData.forEach((node) => {
        filters.forEach(([type, item]) => {
          const field = node[item.field];

          if (field !== undefined && field !== null) {
            item.set.add(field);
          }
        });
      });

      // Assign the set to the original filters as options
      filters.forEach(([type, item]) => {
        this.filters[type].options = [...item.set].sort();
      });
    },

    /**
    *  Reset all filters
    */
    resetFilters() {
      this.resetting = true;

      Object.entries(this.filters)
        .forEach(([type, item]) => {
          this.filters[type].value = [];
        });

      this.$nextTick(() => {
        this.resetting = false;
      });

      if (this.gridApi) {
        this.gridApi.setQuickFilter(null);
        this.gridApi.setFilterModel(this.gridDefaults.filter);
      }

      this.query.search = '';
      this.$store.commit('setPlanItemsToEdit', null);
    },

    /**
    * Remove filter by type + index
    * @param type
    * @param index
    */
    removeFilter({ type, index }) {
      if (!Array.isArray(this.filters[type].value)) {
        this.filters[type].value = [];
      } else {
        this.filters[type].value.splice(index, 1);
      }

      if (this.filters[type].field === 'workPlanName') {
        this.$store.commit('setPlanItemsToEdit', null);
      }

      if (this.filters[type].type === FILTER_SET) {
        this.filterSet(this.filters[type]);
      } else if (this.filters[type].type === FILTER_TEXT) {
        this.filterText(this.filters[type]);
      }

      this.gridApi.onFilterChanged();
    },

    applyModalFilters() {
      Object.entries(this.filters)
        .forEach(([type, item]) => {
          this.filters[type].value = this.filters[type].modalValue;
        });

      this.applyFilters();
    },

    applyFilters() {
      Object.entries(this.filters)
        .forEach(([type, item]) => {
          if (item.type === FILTER_SET) {
            this.filterSet(item);
          } else if (item.type === FILTER_TEXT) {
            this.filterText(item);
          }
        });

      this.gridApi.onFilterChanged();
    },

    onModalFilterShow() {
      Object.entries(this.filters)
        .forEach(([type, item]) => {
          this.filters[type].modalValue = this.filters[type].value;
        });
    },

    refreshTagFilter() {
      this.filters.tag.options = this.optionTags;
    },


    /**
     * Validate the rows selected before going to the bulk query modal
     * @return boolean
     */
    validateQueries() {
      this.validNodes = [];
      this.invalidNodes = [];

      const notAllowedStatuses = [
        ActionStatus.UNASSIGNED(),
        ActionStatus.APPROVED(),
      ];

      let teamSelected = null;

      const selectedNodes = this.getSelectedNodes();
      // We use every here, so we can break out of the loop
      const successfulSelection = selectedNodes
        .every((node) => {
          const { activeTeamId, status } = node.data;

          if (!teamSelected) {
            teamSelected = activeTeamId;
          }

          // If we have gone through the first row, and the team is not the same as the others
          // Then cancel. They must all be the same
          if (teamSelected && activeTeamId !== teamSelected) {
            this.$notify({
              title: 'All actions selected must belong to the same team',
              text: 'Bulk query actions must be from a single team only',
              type: 'warn',
            });
            return false;
          }

          if (notAllowedStatuses.includes(status)) {
            this.invalidNodes.push(node);
            node.setSelected(false);
            return true;
          }

          this.validNodes.push(node);
          return true;
        });

      if (!successfulSelection) {
        return false;
      }

      if (this.validNodes.length === 0) {
        this.$notify({
          title: 'No valid actions selected',
          text: 'Please select actions that have the status of \'Assigned\'',
          type: 'error',
        });

        return false;
      }

      const invalidActionsCount = this.invalidNodes.length;
      if (invalidActionsCount > 0) {
        this.$notify({
          title: 'Some actions have been unselected',
          text: `${invalidActionsCount} actions are not in the correct status and have been unselected.`,
          type: 'error',
        });
      }

      return true;
    },

    validateApprove() {
      this.validNodes = [];
      this.invalidNodes = [];

      const adminBulk = this.$can(
        this.$perm.ACTIONS_BULK_APPROVE_ADMIN,
        this.$perm.PERMISSION_TYPE_ACTION,
      );

      this.invalidStatus = ActionStatus.PENDING_APPROVAL();

      if (adminBulk) {
        this.invalidStatus = ActionStatus.APPROVED();
      }

      this.getSelectedNodes()
        .forEach((node) => {
          if (node.data.status !== ActionStatus.PENDING_APPROVAL()
              && !adminBulk
          ) {
            this.invalidNodes.push(node);
            return true;
          }

          if (adminBulk && node.data.status === ActionStatus.APPROVED()) {
            this.invalidNodes.push(node);
            return true;
          }

          this.validNodes.push(node);
        });

      if (this.validNodes.length === 0) {
        this.$notify({
          title: 'No valid actions selected',
          text: 'Please select actions that have the status of \'Assigned\'',
          type: 'error',
        });

        return false;
      }

      this.invalidNodes.forEach(node => node.setSelected(false));

      return true;
    },

    validateUnapprove() {
      this.validNodes = [];
      this.invalidNodes = [];

      this.getSelectedNodes()
        .forEach((node) => {
          if (node.data.status !== ActionStatus.APPROVED()) {
            this.invalidNodes.push(node);
            return true;
          }
          this.validNodes.push(node);
        });

      if (this.validNodes.length === 0) {
        this.$notify({
          title: 'No valid actions selected',
          text: 'Please select actions that have the status of \'Approved\'',
          type: 'error',
        });

        return false;
      }

      this.invalidNodes.forEach(node => node.setSelected(false));

      return true;
    },

    validateWorkPlan() {
      this.validNodes = [];
      this.invalidNodes = [];

      this.getSelectedNodes()
        .forEach((node) => {
          if (node.data.status === ActionStatus.APPROVED()) {
            this.invalidNodes.push(node);
            return true;
          }
          this.validNodes.push(node);
        });

      if (this.validNodes.length === 0) {
        this.$notify({
          title: 'No valid actions selected',
          text: 'Please select actions that do not have the status of \'Approved\'',
          type: 'error',
        });

        return false;
      }

      return true;
    },

    validateWorkPlanToRemove() {
      this.validNodes = [];

      this.getSelectedNodes()
        .forEach((node) => {
          if (node.data.workPlanId) {
            this.validNodes.push(node);
          }
        });

      if (this.validNodes.length === 0) {
        this.$notify({
          title: 'No valid actions selected',
          text: 'Please select actions that have a planned work assignment.',
          type: 'error',
        });

        return false;
      }

      return true;
    },

    openActionModal(node) {
      // Don't open modal if you mis click on a checkbox cell
      if (node.column.colDef.checkboxSelection) {
        return;
      }

      const actionId = (node.data) ? node.data.clientActionId : 0;

      if (!actionId) {
        return;
      }

      if (this.openClientActionListModal(node, actionId)) {
        return;
      }

      this.$router.push({
        name: 'actionPage',
        params: { clientActionId: actionId },
      });
    },

    openClientActionListModal(node, actionId) {
      if (LINKED_CLIENT_ACTIONS_COLUMN !== node.column.colDef.field
        || !node.data.numOfLinkedClientActions) {
        return false;
      }

      this.modals.clientActionsList.actionId = actionId;
      this.modals.clientActionsList.visible = true;
      return true;
    },

    async addClientAction() {
      const selectedRows = this.getSelectedNodes();

      if (selectedRows.length === 0) {
        this.$notify({
          title: 'No actions selected',
          text: 'When adding a new client action, please select an action to link to',
          type: 'error',
        });

        return;
      }

      if (selectedRows.length > 1) {
        this.$notify({
          title: 'Multiple actions selected',
          text: 'When adding a new client action, please select a maximum of one action',
          type: 'error',
        });

        return;
      }

      const selectedRow = selectedRows[0];
      if (selectedRow) {
        this.modals.add.linkedAction = selectedRow.data;
        this.modals.add.property = {
          propertyId: selectedRow.data.propertyId,
          uprn: selectedRow.data.uprn,
        };
      }

      this.openModal('add');
    },

    /**
       * Opens a modal by name (in data -> modals.[name])
       */
    openModal(name) {
      if (this.refreshing) {
        this.$notify({
          title: 'Please wait..',
          text: 'Actions are currently being loaded, please try again',
          type: 'warn',
        });

        return false;
      }

      if (this.selectedIds.length === 0) {
        this.$notify({
          title: 'No actions selected',
          text: 'Please select at least one action.',
          type: 'warn',
        });

        return false;
      }

      if (name === 'approve' && !this.validateApprove()) {
        return false;
      }

      if (name === 'unapprove' && !this.validateUnapprove()) {
        return false;
      }
      if (name === 'queries' && !this.validateQueries()) {
        return false;
      }
      if (name === 'actionWorkPlan' && !this.validateWorkPlan()) {
        return false;
      }

      if (name === 'removeFromWorkPlan' && !this.validateWorkPlanToRemove()) {
        return false;
      }

      if (name === 'note') {
        this.setNoteDefaults();
        this.modals.note.settings.sentNoteFn = this.addNote;
        this.modals.note.settings.notePlaceholder = 'Write a note...';
        this.modals.note.settings.noteErrorMessage = 'Please enter a note';
        this.modals.note.settings.fileUrl = `${api.defaults.baseURL}/actions/riskhub/note`;
        this.modals.note.settings.onSentFileFn = this.onSuccessfulNote;
        this.modals.note.settings.showNoteModal = true;
      }

      if (name === CLIENT_ACTION_LIST_MODAL_ID && this.selectedIds.length === 1) {
        this.modals.clientActionsList.actionId = this.selectedIds[0];
        this.modals.clientActionsList.visible = true;
        return false;
      }

      this.modals[name].visible = true;
      return true;
    },
    /**
     * Hide a modal by name
     * @param string name
    */
    hideModal(name) {
      this.modals[name].visible = false;
    },

    /**
     * Add a note against an action
     */
    async addNote({ note }) {
      try {
        await Action.bulkAddNote(
          this.getSelectedNodes().map(node => node.data[this.modelId]),
          note,
        );

        this.onSuccessfulNote();
      } catch (e) {
        this.showErrorMessage(e);
      } finally {
        this.modals.note.loading = false;
      }
    },

    setNoteDefaults() {
      this.modals.note.settings.noteTitle = 'Add a note';
      this.modals.note.settings.noteButtonText = 'Send note';
      this.modals.note.settings.notePlaceholder = null;
      this.modals.note.settings.noteErrorMessage = null;
      this.modals.note.settings.fileUrl = null;
    },

    onSuccessfulNote() {
      const title = 'Note successfully added';
      const text = '';
      const type = 'success';

      this.$notify({
        duration: 7000,
        title,
        text,
        type,
      });

      this.modals.note.visible = false;
    },

    /**
       * @returns {boolean}
       */
    onClickDownloadPdf() {
      let reviewer = '';

      const teams = this.filters.team.value;
      const users = this.filters.user.value;

      if (teams.length > 0) {
        reviewer = teams.join(', ');
      }

      if (users.length > 0) {
        reviewer += users.join(', ');
      }

      if (reviewer === '') {
        this.$notify({
          title: 'No user or teams selected',
          text: 'Please select at least one user or team to filter by',
          type: 'warn',
        });
        return false;
      }

      const actionIds = [];

      this.gridApi.forEachNodeAfterFilter((node) => {
        if (node.data) {
          actionIds.push(node.data.clientActionId);
        }
      });

      const params = {
        clientActionIds: actionIds,
        reviewer,
      };

      this.$notify({
        title: 'PDF processing...',
        text: 'Please wait while the PDF is being created',
        type: 'warn',
        duration: 6000,
      });

      Action.downloadPdf(params);
      return true;
    },

    onClickDownloadCsv() {
      const processCellCallback = (params) => {
        const column = params.column.getColId();
        let { value } = params;

        switch (column) {
          case 'query':
            value = value.replace(/<br \/>/g, '');
            value = value.replace(/\n/g, '');
            value = value.replace(/\r/g, ' ');
            break;

          case 'photosBtn':
            value = (value) ? 'Y' : 'N';
            break;

          case 'numberRange':
          case 'streetNumber':
            if (value) {
              value = `="${value}"`;
            }
            break;

          case 'dueDate':
          case 'internalDueDate':
          case 'handoverDate':
          case 'workPlanDueDate':
          case 'lastStatusUpdatedDate':
          case 'completedDate':
          case 'submittedForApprovalDate':
            value = riskhub.formatDate(value, true) || 'N/A';
            break;
          case 'specification':
          case 'recommendation':
          case 'tenure':
          case 'comments':
          case 'notes':
            if (value) {
              // Remove all kinds of line breaks
              value = value.replace(/(\r\n|\n|\r)/gm, '');
            }
            break;
        }

        return value;
      };

      const onlySelected = this.getSelectedNodes().length > 0;
      const params = {
        processCellCallback,
        onlySelected,
      };

      this.gridApi.exportDataAsCsv(params);
    },

    async onClickDownloadAll() {
      await Action.downloadAll(this.$store.getters.complianceTypeId);
      this.$notify({
        title: 'Creating download',
        text: 'A download of your actions is being created and will be emailed to you shortly',
        type: 'success',
        duration: 6000,
      });
    },
    showActionsDropdown() {
      return this.$can(this.$perm.ACTIONS_ADD_CLIENT_ACTIONS, this.$perm.PERMISSION_TYPE_ACTION)
        || this.$can(this.$perm.ACTIONS_ASSIGN, this.$perm.PERMISSION_TYPE_ACTION)
        || this.$can(this.$perm.ACTIONS_BULK_APPROVE_SUBMITTED, this.$perm.PERMISSION_TYPE_ACTION)
        || this.$can(this.$perm.ACTIONS_BULK_APPROVE_ADMIN, this.$perm.PERMISSION_TYPE_ACTION)
        || this.$can(this.$perm.ACTIONS_TAG, this.$perm.PERMISSION_TYPE_ACTION)
        || this.$can(this.$perm.ACTIONS_DOWNLOAD_TO_CSV, this.$perm.PERMISSION_TYPE_ACTION)
        || this.$can(this.$perm.ACTIONS_DOWNLOAD_ALL_ACTIONS, this.$perm.PERMISSION_TYPE_ACTION)
        || this.showDownloadPdf;
    },
    uppercaseFirst(string) {
      if (!string) {
        return '';
      }

      return string.charAt(0)
        .toUpperCase() + string.slice(1);
    },
    submitSearchBox() {
      if (this.requiresServersideSearch) {
        this.refresh();
      }
    },
    changeDateRange(value, index) {
      this.yearRange = value;
      this.selectedYearFilterIndex = index;
      this.refresh();
    },
    formatDate(date) {
      return dayjs(date).format('DD MMM YYYY');
    },
    async resetPage() {
      if (this.loading) {
        return;
      }

      this.loading = true;

      this.deselectAll();
      this.resetFilters();
      await this.setGridColumns();
      await this.refresh();

      this.loading = false;
    }
  },
};
</script>

<style lang="scss" scoped>
    .filters-toggle {
        min-width: 130px;
    }
    .filters-top {
        display: flex;
        font-size: 14px;
        justify-content: flex-end;

        > div {
            margin-left: 15px;
        }

        .multiselect {
            max-width: 220px;
            min-width: 150px;
            min-height: 36px;

            /deep/ &__input {
                font-size: 15px;
                border-color: $border-color;
            }

            /deep/ &__placeholder {
                display: none;
            }

            /deep/ &__caret {
                top: 5.5px !important;
            }
        }
        /deep/ .multiselect__tags {
            border-color: $border-color;
            padding-top: 0.2rem !important;
            padding-bottom: 0.2rem !important;
            padding-left: 0.2rem !important;
            min-height: 14px;
        }
    }
    .filters-all {
        .filters-reset {
            color: #848484;
            cursor: pointer;
            text-decoration: underline;
        }

        .badge {
            margin-right: 0.75rem;
            font-size: 14px;
            padding: 5px 10px;
            font-weight: $font-weight-base;
            cursor: pointer;

            .fa-times-circle {
                margin-left: 5px;
            }
        }
    }
    @media screen and (max-width: 992px) {
        .filters-top {
            > div {
                margin-left: 5px;
            }

            .multiselect {
                /deep/ &__input {
                    font-size: 12px;
                }
            }

            /deep/ .multiselect__tags {
                padding-left: 0.2rem !important;
            }
        }
    }
</style>
